<script setup>
import Dialog from 'primevue/dialog';
import {computed, provide, inject, onMounted, ref} from "vue";
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {Icon} from "@iconify/vue";
import {showMessage} from "@/utils/showMessage.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useUserStore} from "@/stores/user.js";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import {gregorianDate} from "@/utils/gregorianDate.js";
import UserProfileSkeleton from "@/components/UserProfileSkeleton.vue";
import {Tab, TabList, TabPanel, TabPanels, Tabs, Tag} from "primevue";
import {useConfigStore} from "@/stores/config.js";
import History from "@/components/UserProfile/history.vue"
import DiscordInfo from "@/components/UserProfile/discordInfo.vue"
import KYCInfo from "@/components/UserProfile/kycInfo.vue"
import PaymentInfo from "@/components/UserProfile/PaymentInfoModal.vue";
import VerificationNoteModal from "@/components/UserProfile/VerificationNoteModal.vue";
import {useManagementStore} from "@/stores/management.js";

//stores
const userStore = useUserStore();
const configStore = useConfigStore();
const managementStore = useManagementStore();

//loading flags
const {
  fetching,
  saving,
  deleting,
  setFetching,
  setSaving,
  setDeleting,
  resetAll,
} = useLoadingFlags()

//other composables
const {isXL} = useBreakpoint();

//emits
const emit = defineEmits(['refresh', 'showUserPermissions', 'showUserRoles']);

//props
const props = defineProps({
  userID: Number,
  manageDiscordInfo: {
    default: false,
    type: Boolean
  },
  managePermissions: {
    default: false,
    type: Boolean
  },
  manageKYC: {
    default: false,
    type: Boolean
  },
  managePayment: {
    default: false,
    type: Boolean
  },
  manageVerification: {
    default: false,
    type: Boolean
  }
})

//injects
const visible = inject('isShowUserProfileModal')

//provides

//component variables
const canEditDiscordInfo = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))
const canEditPermissions = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('USER_MANAGEMENT_UPDATE'))
const canEditKYCInfo = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('KYC_MANAGEMENT'))
const canEditPaymentInfo = computed(() => userStore.hasPermission('BIG_BOSS'))

const user = ref({})
const activeTab = ref("0")
const maximized = ref(false)
const isShowDiscordInfoModal = ref(false)
const isShowKYCInfoModal = ref(false)
const isShowPaymentInfoModal = ref(false)
const isShowVerificationNoteModal = ref(false)
const rejectLoading = ref(false);
const approveLoading = ref(false);

const sortedTags = computed(() => {
  if (!user.value?.tags) return [];
  return Object.entries(user.value.tags).sort((a, b) => b[1] - a[1]); // descending by value
});
const myRoles = [
  {label: "Albion Online - Boost", value: "Albion Online - Boost", service: "Boost"},
  {label: "Albion Online - Currency", value: "Albion Online - Currency", service: "Currency"},
  {label: "Albion Online - Account", value: "Albion Online - Account", service: "Account"},
  {label: "Apex Legends - Boost", value: "Apex Legends - Boost", service: "Boost"},
  {label: "Apex Legends - Account", value: "Apex Legends - Account", service: "Account"},
  {label: "ARC Raiders - Boost", value: "ARC Raiders - Boost", service: "Boost"},
  {label: "ARC Raiders - Account", value: "ARC Raiders - Account", service: "Account"},
]

//global functions


//component functions
const getBadgeClass = (item) => {
  const bgClass = {
    Account: "!bg-[#2196F3]",
    Boost: "!bg-[#4CAF50]",
    Currency: "!bg-[#FF6000]",
    Item: "!bg-[#795548]",
    "Gift Card": "!bg-[#E91E63]",
  }[item.service] || "";

  return `${bgClass}`;
};

const fetchInitialData = async (showLoading = true) => {
  if (showLoading) {
    setFetching(true)
  }
  try {
    const res = await userStore.action_getCompleteProfile3({id: props.userID})
    if (res.result === true) {
      user.value = res.data
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    setFetching(false)
  }
}

const showDiscordInfoModal = () => {
  isShowDiscordInfoModal.value = true
}

const showKYCInfoModal = () => {
  isShowKYCInfoModal.value = true
}

const showPaymentInfoModal = () => {
  isShowPaymentInfoModal.value = true
}

const showVerificationNoteModal = () => {
  isShowVerificationNoteModal.value = true
}

const changeVerificationState = async (state, note = null) => {
  if (state === 'Rejected') {
    rejectLoading.value = true
  } else {
    approveLoading.value = true
  }
  const res = await managementStore.action_changeVerificationState({
    "id": user.value.id,
    "verificationState": state,
    "verificationNote": note
  })
  if (res.result === true) {
    showMessage(`User ${state}!`, 'success');
    await fetchInitialData(false)
    isShowVerificationNoteModal.value = false
    emit('refresh')
    setTimeout(() => {
      visible.value = false
    }, 200)
  } else {
    showMessage(res, 'error')
  }
  rejectLoading.value = false
  approveLoading.value = false
}

const setVerificationNote = (note) => {
  changeVerificationState('Rejected', note)
}

//hooks
onMounted(() => {
  fetchInitialData(true)
})

provide('isShowDiscordInfoModal', isShowDiscordInfoModal)
provide('isShowKYCInfoModal', isShowKYCInfoModal)
provide('isShowPaymentInfoModal', isShowPaymentInfoModal)
provide('isShowVerificationNoteModal', isShowVerificationNoteModal)
</script>

<template>
  <Dialog v-model:visible="visible"
          :class="['lg:min-w-max !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal :maximizable="activeTab === '1'"
          @maximize="() => {maximized = true}"
          @unmaximize="() => {maximized = false}"
          :content-class="[
  'flex flex-col gap-4 !overflow-hidden h-full w-full',
  maximized ? '' : 'lg:max-w-3xl max-h-[calc(100vh-100px)]'
].join(' ')"

          :pt="{header: { class: '!h-max' }}">
    <template #header>
      <div class="text-base py-3 !h-max">
        User Profile
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div :class="['flex-1 flex flex-col gap-4 pt-4 text-xs 2xl:text-sm overflow-y-hidden']">
      <Tabs v-model:value="activeTab" class="custom-tabs flex flex-col h-full overflow-hidden">
        <div class="flex flex-row justify-between items-center border-b border-slate-700 pb-2">
          <TabList>
            <Tab value="0">Details</Tab>
            <Tab value="1">History</Tab>
          </TabList>
        </div>
        <TabPanels
            :class="['flex-1 flex flex-col', activeTab === '1' ? 'overflow-y-hidden' : 'overflow-y-auto custom-scrollbar']">
          <TabPanel value="0">
            <div class="flex-1 flex flex-col gap-4 overflow-y-auto pr-1">
              <UserProfileSkeleton v-if="fetching"/>
              <template v-else>
                <div class="flex flex-col md:flex-row gap-4">
                  <div class="flex flex-col gap-2 items-center justify-center border border-slate-800 rounded-md p-2">
                    <div class="flex flex-col justify-center items-center text-xs">
                      <span class="text-slate-400">Username</span>
                      <span>{{ user.username || "-" }}</span>
                    </div>
                    <div class="p-4">
                      <img v-if="user.discordAvatar" :alt="user.username || '-'"
                           :src="user.discordAvatar"
                           class="size-32 rounded-full border-4 border-gray-500"/>
                      <div v-else
                           class="flex items-center justify-center size-32 rounded-full border-4 border-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                             class="lucide lucide-user-icon lucide-user h-24 w-24">
                          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                          <circle cx="12" cy="7" r="4"/>
                        </svg>
                      </div>
                    </div>
                    <div v-if="user.boostOrdersRating"
                         class="flex flex-row items-center gap-0.5 text-xs">
                      <span class="flex flex-row items-end">{{
                          user.boostOrdersRating
                        }}⭐ ({{ user.boostOrdersRatingCount }})</span>
                    </div>
                  </div>
                  <div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8 border border-slate-800 rounded-md p-4">
                    <!-- Basic Info -->
                    <div class="col-span-1 flex flex-col gap-4">
                      <div class="flex flex-row gap-2 justify-between border-b border-slate-700 pb-1">
                        <span>Basic Info</span>
                        <span v-if="managePermissions && canEditPermissions"
                              @click="() => {emit('showUserPermissions', user)}"
                              class="cursor-pointer">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                  d="M9.75684 1.98286C9.76631 2.39697 9.43829 2.74034 9.02418 2.74981C6.20248 2.81432 4.87348 3.09895 3.99627 3.97617C3.42588 4.54656 3.09862 5.31824 2.92655 6.59801C2.75159 7.89936 2.75 9.60936 2.75 11.99C2.75 14.3705 2.75159 16.0805 2.92655 17.3819C3.09862 18.6617 3.42588 19.4333 3.99627 20.0037C4.56666 20.5741 5.33834 20.9014 6.61811 21.0735C7.91946 21.2484 9.62946 21.25 12.0101 21.25C14.3906 21.25 16.1006 21.2484 17.402 21.0735C18.6818 20.9014 19.4534 20.5741 20.0238 20.0037C20.9011 19.1265 21.1857 17.7975 21.2502 14.9758C21.2597 14.5617 21.603 14.2337 22.0171 14.2432C22.4312 14.2526 22.7593 14.596 22.7498 15.0101C22.6866 17.7765 22.4374 19.7115 21.0845 21.0644C20.1889 21.9599 19.048 22.3657 17.6019 22.5601C16.189 22.75 14.3782 22.75 12.0674 22.75H11.9527C9.64191 22.75 7.8311 22.75 6.41824 22.5601C4.97214 22.3657 3.83115 21.9599 2.93561 21.0644C2.04006 20.1689 1.63435 19.0279 1.43993 17.5818C1.24998 16.1689 1.24999 14.3581 1.25 12.0473V11.9326C1.24999 9.62181 1.24998 7.811 1.43993 6.39814C1.63435 4.95204 2.04006 3.81105 2.93561 2.91551C4.2885 1.56262 6.22347 1.31345 8.98989 1.2502C9.404 1.24073 9.74737 1.56875 9.75684 1.98286ZM17.2606 2.75C15.0544 2.75 13.2711 4.53036 13.2711 6.72008C13.2711 7.21172 13.3839 7.64295 13.4996 7.95426C13.557 8.10864 13.6137 8.22949 13.6541 8.30858C13.6742 8.348 13.6901 8.37665 13.6997 8.39349L13.7091 8.40964C13.8944 8.70485 13.8506 9.08899 13.6037 9.33493L7.87005 15.0451C7.85527 15.0598 7.82684 15.0936 7.80049 15.1408C7.77466 15.1871 7.75916 15.2314 7.75308 15.2686C7.74754 15.3025 7.74952 15.3303 7.75842 15.358C7.76713 15.3852 7.79159 15.4409 7.87005 15.519L8.50421 16.1506C8.52318 16.1647 8.55267 16.184 8.59 16.2022C8.63879 16.2261 8.68606 16.2411 8.72624 16.2471C8.76432 16.2527 8.78981 16.2495 8.80787 16.2441C8.82312 16.2395 8.85657 16.2266 8.9057 16.1777L9.67724 15.4093C9.96988 15.1178 10.4431 15.1178 10.7357 15.4093C10.8507 15.5237 10.9465 15.5714 11.0148 15.5922C11.0838 15.6133 11.1522 15.6155 11.2227 15.602C11.379 15.5721 11.5211 15.4687 11.5865 15.386C11.677 15.2553 11.6741 15.1418 11.6308 15.0124C11.6067 14.9407 11.5721 14.876 11.5419 14.8284C11.5274 14.8055 11.5155 14.7892 11.5091 14.7808C11.5074 14.7785 11.5054 14.7759 11.5054 14.7759C11.2557 14.478 11.2748 14.0378 11.551 13.7627L11.7714 13.5432C12.0641 13.2518 12.5373 13.2518 12.8299 13.5432C13.2139 13.9256 13.4684 13.9208 13.591 13.8976C13.7642 13.8647 13.9255 13.7402 14.011 13.6301C14.1236 13.4689 14.1241 13.3249 14.0925 13.1925C14.0751 13.1199 14.0481 13.0565 14.0253 13.0119C14.0142 12.9903 14.0053 12.9753 14.0009 12.9683L13.9999 12.9668L13.9714 12.927L13.945 12.8744C13.9355 12.8554 13.9203 12.8325 13.8718 12.779C13.8702 12.7772 13.8672 12.7741 13.863 12.7697C13.826 12.7307 13.6958 12.5938 13.6159 12.4504C13.4791 12.2052 13.4412 11.9068 13.5599 11.602C13.6513 11.3672 13.82 11.1737 13.9758 11.0185L14.6371 10.3599C14.9112 10.087 15.3478 10.0672 15.6454 10.3142C15.765 10.4135 16.0204 10.5205 16.3848 10.596C16.7271 10.6668 17.067 10.6902 17.2606 10.6902C19.4668 10.6902 21.25 8.9098 21.25 6.72008C21.25 4.53036 19.4668 2.75 17.2606 2.75ZM13.1486 15.3763C13.1174 15.6615 13.0159 15.9669 12.7999 16.268L12.7948 16.2751L12.7895 16.2821C12.5219 16.6375 12.061 16.9687 11.5049 17.0752C11.1118 17.1505 10.6919 17.1088 10.2913 16.9147L9.96419 17.2405C9.51185 17.691 8.97167 17.7996 8.50691 17.7309C8.07947 17.6678 7.727 17.4603 7.51517 17.2795C7.50058 17.267 7.48647 17.254 7.47288 17.2405L6.81156 16.5819C6.32963 16.1019 6.18913 15.5381 6.27272 15.0266C6.34864 14.5622 6.598 14.195 6.81156 13.9823L12.1628 8.65293C12.14 8.59793 12.1168 8.53926 12.0937 8.47711C11.9339 8.04748 11.7711 7.4359 11.7711 6.72008C11.7711 3.69615 14.2317 1.25 17.2606 1.25C20.2894 1.25 22.75 3.69615 22.75 6.72008C22.75 9.74401 20.2894 12.1902 17.2606 12.1902C16.9765 12.1902 16.5339 12.1586 16.0807 12.0648C15.84 12.015 15.5614 11.9408 15.2909 11.8258L15.1456 11.9705C15.1844 12.0246 15.224 12.0862 15.2615 12.1554C15.2657 12.1619 15.2702 12.1689 15.2748 12.1764C15.2984 12.2144 15.3283 12.2655 15.3603 12.328C15.4235 12.4514 15.4998 12.6282 15.5514 12.8437C15.657 13.2855 15.6581 13.9075 15.2247 14.5117L15.2196 14.5188L15.2144 14.5258C14.9678 14.8532 14.5039 15.2511 13.8705 15.3713C13.6342 15.4161 13.392 15.4191 13.1486 15.3763ZM17.2606 5.82354C16.7588 5.82354 16.3573 6.22783 16.3573 6.72008C16.3573 7.21233 16.7588 7.61662 17.2606 7.61662C17.7623 7.61662 18.1639 7.21233 18.1639 6.72008C18.1639 6.22783 17.7623 5.82354 17.2606 5.82354ZM14.8573 6.72008C14.8573 5.39362 15.9362 4.32354 17.2606 4.32354C18.585 4.32354 19.6639 5.39362 19.6639 6.72008C19.6639 8.04654 18.585 9.11662 17.2606 9.11662C15.9362 9.11662 14.8573 8.04654 14.8573 6.72008Z"
                                  fill="currentColor"/>
                          </svg>
                        </span>
                      </div>
                      <div class="flex-1 flex flex-col gap-2">
                        <div class="flex flex-col">
                          <span class="text-slate-400 text-xs">Name</span>
                          <span>{{ user.name ?? "ــــــ" }}</span>
                        </div>
                        <div class="flex flex-col">
                          <span class="text-slate-400 text-xs">User ID</span>
                          <span>{{ user.id ?? "ــــــ" }}</span>
                        </div>
                        <div class="flex flex-col">
                          <span class="text-slate-400 text-xs">Active State</span>
                          <span>{{ user.isActive ? 'Active' : 'Not Active' }}</span>
                        </div>
                        <div class="flex flex-col">
                          <span class="text-slate-400 text-xs">Last Update</span>
                          <span>{{ gregorianDate(user.lastUpdate, 'DD MMM YYYY - HH:mm') }}</span>
                        </div>
                      </div>
                    </div>
                    <!-- Discord -->
                    <div class="col-span-1 flex flex-col gap-4">
                      <div class="flex flex-row gap-2 justify-between border-b border-slate-700 pb-1">
                        <span>Discord Info</span>
                        <span v-if="manageDiscordInfo && canEditDiscordInfo" @click="showDiscordInfoModal"
                              class="cursor-pointer">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path
                              fill="currentColor" fill-rule="evenodd"
                              d="M11.943 1.25H13.5a.75.75 0 0 1 0 1.5H12c-2.378 0-4.086.002-5.386.176c-1.279.172-2.05.5-2.62 1.069c-.569.57-.896 1.34-1.068 2.619c-.174 1.3-.176 3.008-.176 5.386s.002 4.086.176 5.386c.172 1.279.5 2.05 1.069 2.62c.57.569 1.34.896 2.619 1.068c1.3.174 3.008.176 5.386.176s4.086-.002 5.386-.176c1.279-.172 2.05-.5 2.62-1.069c.569-.57.896-1.34 1.068-2.619c.174-1.3.176-3.008.176-5.386v-1.5a.75.75 0 0 1 1.5 0v1.557c0 2.309 0 4.118-.19 5.53c-.194 1.444-.6 2.584-1.494 3.479c-.895.895-2.035 1.3-3.48 1.494c-1.411.19-3.22.19-5.529.19h-.114c-2.309 0-4.118 0-5.53-.19c-1.444-.194-2.584-.6-3.479-1.494c-.895-.895-1.3-2.035-1.494-3.48c-.19-1.411-.19-3.22-.19-5.529v-.114c0-2.309 0-4.118.19-5.53c.194-1.444.6-2.584 1.494-3.479c.895-.895 2.035-1.3 3.48-1.494c1.411-.19 3.22-.19 5.529-.19m4.827 1.026a3.503 3.503 0 0 1 4.954 4.953l-6.648 6.649c-.371.37-.604.604-.863.806a5.3 5.3 0 0 1-.987.61c-.297.141-.61.245-1.107.411l-2.905.968a1.492 1.492 0 0 1-1.887-1.887l.968-2.905c.166-.498.27-.81.411-1.107q.252-.526.61-.987c.202-.26.435-.492.806-.863zm3.893 1.06a2.003 2.003 0 0 0-2.832 0l-.376.377q.032.145.098.338c.143.413.415.957.927 1.469a3.9 3.9 0 0 0 1.807 1.025l.376-.376a2.003 2.003 0 0 0 0-2.832m-1.558 4.391a5.4 5.4 0 0 1-1.686-1.146a5.4 5.4 0 0 1-1.146-1.686L11.218 9.95c-.417.417-.58.582-.72.76a4 4 0 0 0-.437.71c-.098.203-.172.423-.359.982l-.431 1.295l1.032 1.033l1.295-.432c.56-.187.779-.261.983-.358q.378-.18.71-.439c.177-.139.342-.302.759-.718z"
                              clip-rule="evenodd"/></svg>
                        </span>
                      </div>
                      <div class="flex-1 flex flex-col gap-2">
                        <div class="flex flex-col">
                          <span class="text-slate-400 text-xs">ID</span>
                          <span>{{ user.discordID ?? "ــــــ" }}</span>
                        </div>
                        <div class="flex flex-col">
                          <span class="text-slate-400 text-xs">Username</span>
                          <span>{{ user.discordUsername ?? "ــــــ" }}</span>
                        </div>
                        <div class="flex flex-col">
                          <span class="text-slate-400 text-xs">Display Name</span>
                          <span>{{ user.discordDisplayName ?? "ــــــ" }}</span>
                        </div>
                        <div class="flex flex-col">
                          <span class="text-slate-400 text-xs">Registration Time</span>
                          <span>{{ user.discordID ? discordRegistrationTime(user.discordID) : "ــــــ" }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="user?.tags"
                     class="flex flex-wrap gap-1 w-full border border-slate-800 rounded-md p-4 relative">
                  <Tag v-for="[key, value] in sortedTags"
                       :severity="configStore.tags[key] === 1 ? 'success' : 'danger'" :value="`${key} (${value})`"
                       class="!font-normal !text-xs"></Tag>
                  <label for="" class="absolute top-0 transform translate-y-[-9px] text-xs">Tags</label>
                </div>
                <!--                <div v-if="myRoles" class="flex flex-row w-full border border-slate-800 rounded-md p-4 relative">
                                  <div class="flex-1 flex flex-wrap gap-1 text-sm">
                                    <p v-for="role in myRoles" :class="['px-2 py-1 text-xs rounded-md',getBadgeClass(role)]">
                                      {{ role.label }}</p>
                                  </div>
                                  <div @click="() => {emit('showUserRoles', user)}" class="cursor-pointer">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                         class="lucide lucide-settings-icon lucide-settings">
                                      <path
                                          d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"/>
                                      <circle
                                          cx="12" cy="12" r="3"/>
                                    </svg>
                                  </div>
                                  <label for="" class="absolute top-0 transform translate-y-[-9px] text-xs">Roles</label>
                                </div>-->
                <div class="flex flex-col gap-4 border border-slate-800 rounded-md p-4">
                  <div class="flex flex-row gap-2 justify-between border-b border-slate-700 pb-1">
                    <span>KYC Info</span>
                    <div class="flex flex-row gap-2">
                      <span v-if="canEditPaymentInfo && managePayment" @click="showPaymentInfoModal"
                            class="cursor-pointer">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                              d="M23 12C23 6.81455 23 4.22183 21.7113 2.61091C20.4225 1 18.3484 1 14.2 1H9.8C5.65164 1 3.57746 1 2.28873 2.61091C1 4.22183 1 6.81455 1 12C1 17.1854 1 19.7782 2.28873 21.3891C3.57746 23 5.65164 23 9.8 23H14.2"
                              stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                          <path d="M8 16H4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                          <path d="M12 16H11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                          <path d="M2 7L22 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                          <path d="M15 19H19M19 19H23M19 19V23M19 19V15" stroke="currentColor" stroke-width="1.5"
                                stroke-linecap="round"/>
                        </svg>
                      </span>
                      <span v-if="canEditKYCInfo && manageKYC" @click="showKYCInfoModal"
                            class="cursor-pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                          <path fill="currentColor" fill-rule="evenodd"
                                d="M11.943 1.25H13.5a.75.75 0 0 1 0 1.5H12c-2.378 0-4.086.002-5.386.176c-1.279.172-2.05.5-2.62 1.069c-.569.57-.896 1.34-1.068 2.619c-.174 1.3-.176 3.008-.176 5.386s.002 4.086.176 5.386c.172 1.279.5 2.05 1.069 2.62c.57.569 1.34.896 2.619 1.068c1.3.174 3.008.176 5.386.176s4.086-.002 5.386-.176c1.279-.172 2.05-.5 2.62-1.069c.569-.57.896-1.34 1.068-2.619c.174-1.3.176-3.008.176-5.386v-1.5a.75.75 0 0 1 1.5 0v1.557c0 2.309 0 4.118-.19 5.53c-.194 1.444-.6 2.584-1.494 3.479c-.895.895-2.035 1.3-3.48 1.494c-1.411.19-3.22.19-5.529.19h-.114c-2.309 0-4.118 0-5.53-.19c-1.444-.194-2.584-.6-3.479-1.494c-.895-.895-1.3-2.035-1.494-3.48c-.19-1.411-.19-3.22-.19-5.529v-.114c0-2.309 0-4.118.19-5.53c.194-1.444.6-2.584 1.494-3.479c.895-.895 2.035-1.3 3.48-1.494c1.411-.19 3.22-.19 5.529-.19m4.827 1.026a3.503 3.503 0 0 1 4.954 4.953l-6.648 6.649c-.371.37-.604.604-.863.806a5.3 5.3 0 0 1-.987.61c-.297.141-.61.245-1.107.411l-2.905.968a1.492 1.492 0 0 1-1.887-1.887l.968-2.905c.166-.498.27-.81.411-1.107q.252-.526.61-.987c.202-.26.435-.492.806-.863zm3.893 1.06a2.003 2.003 0 0 0-2.832 0l-.376.377q.032.145.098.338c.143.413.415.957.927 1.469a3.9 3.9 0 0 0 1.807 1.025l.376-.376a2.003 2.003 0 0 0 0-2.832m-1.558 4.391a5.4 5.4 0 0 1-1.686-1.146a5.4 5.4 0 0 1-1.146-1.686L11.218 9.95c-.417.417-.58.582-.72.76a4 4 0 0 0-.437.71c-.098.203-.172.423-.359.982l-.431 1.295l1.032 1.033l1.295-.432c.56-.187.779-.261.983-.358q.378-.18.71-.439c.177-.139.342-.302.759-.718z"
                                clip-rule="evenodd"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div class="flex flex-col gap-2">
                    <div v-if="user.verificationNote" class="py-1 px-2 bg-red-500/30 rounded-md">Note:
                      {{ user.verificationNote }}
                    </div>
                    <div class="grid grid-cols-2 gap-2">
                      <div class="flex flex-col">
                        <span class="text-slate-400 text-xs">First name</span>
                        <span>{{ user.firstName || "-" }}</span>
                      </div>
                      <div class="flex flex-col">
                        <span class="text-slate-400 text-xs">Last name</span>
                        <span>{{ user.lastName || "-" }}</span>
                      </div>
                    </div>
                    <div class="grid grid-cols-2 gap-2">
                      <div class="flex flex-col">
                        <span class="text-slate-400 text-xs">Phone Number</span>
                        <span>{{ user.phoneNumber || "-" }}</span>
                      </div>
                      <div class="flex flex-col">
                        <span class="text-slate-400 text-xs">Verification State</span>
                        <span v-if="user.verificationState === 'UnderReview'"
                              class="px-3 rounded-md bg-slate-700 w-max">{{ user.verificationState || "-" }}</span>
                        <span v-if="user.verificationState === 'Approved'"
                              class="px-3 rounded-md bg-green-700 w-max">{{ user.verificationState || "-" }}</span>
                        <span v-if="user.verificationState === 'Rejected'"
                              class="px-3 rounded-md bg-red-700 w-max">{{ user.verificationState || "-" }}</span>
                      </div>
                    </div>
                    <div class="flex flex-wrap items-end w-full gap-2">
                      <div class="flex flex-col">
                        <span class="text-slate-400 text-xs">Address</span>
                        <span>{{ user.address || "-" }}</span>
                      </div>

                      <div v-if="manageVerification" class="flex gap-2 ml-auto text-xs">
                        <button @click="changeVerificationState('Approved')"
                                class="px-3 py-1 rounded-md bg-green-700 flex items-center justify-center gap-3 disabled:opacity-50">
                          <Icon v-if="approveLoading" icon="eos-icons:bubble-loading" height="20"/>
                          Approve
                        </button>
                        <button @click="showVerificationNoteModal" class="px-3 py-1 rounded-md bg-red-700">
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </TabPanel>
          <TabPanel value="1" selectOnFocus class="flex-1 flex flex-col overflow-y-hidden">
            <div class="flex-1 flex flex-col gap-4 overflow-y-hidden">
              <History v-if="activeTab === '1'" :user="user" :maximized="maximized"/>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </Dialog>
  <DiscordInfo v-if="isShowDiscordInfoModal" :user="user" @refresh="fetchInitialData(false)"/>
  <KYCInfo v-if="isShowKYCInfoModal" :user="user" @refresh="fetchInitialData(false)"/>
  <PaymentInfo v-if="isShowPaymentInfoModal" :user="user" @refresh="fetchInitialData(false)"/>
  <VerificationNoteModal v-if="isShowVerificationNoteModal" :user="user" @reject="setVerificationNote"
                         :rejectLoading="rejectLoading"/>
</template>

<style scoped>
</style>
